import { useEffect, useMemo, useState } from 'react';
import type { Task } from '../../types/crm';
import { useCrmData } from '../../contexts/CrmDataContext';
import { canCreatePersonalCrmRecords } from '../../lib/roleAccess';
import {
  listTaskMonthOptions,
  pickDefaultTaskChartMonth,
} from '../../utils/taskHelpers';
import { TaskFormModal } from '../tasks/TaskFormModal';
import { DashboardTaskList } from './DashboardTaskList';
import { DashboardTaskTitlePie } from './DashboardTaskTitlePie';

type DashboardTasksPanelProps = {
  tasks: Task[];
};

export function DashboardTasksPanel({ tasks }: DashboardTasksPanelProps) {
  const { getMyData, effectiveRole } = useCrmData();
  const [chartMonth, setChartMonth] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const canCreateTask = canCreatePersonalCrmRecords(effectiveRole);

  const scopeTasks = useMemo(() => [...getMyData('tasks')], [getMyData]);
  const monthOptions = useMemo(() => listTaskMonthOptions(scopeTasks), [scopeTasks]);

  useEffect(() => {
    if (monthOptions.length === 0) {
      setChartMonth('');
      return;
    }
    if (chartMonth && monthOptions.some((option) => option.value === chartMonth)) return;
    const preferred = pickDefaultTaskChartMonth(monthOptions, scopeTasks);
    if (preferred) setChartMonth(preferred);
  }, [monthOptions, chartMonth, scopeTasks]);

  const searchTrimmed = search.trim();

  return (
    <>
      <div className="dashboard-layout__split-row">
        <section className="card dashboard-panel dashboard-panel--breakdown">
          <div className="dashboard-panel__head dashboard-panel__head--with-period">
            <h2 className="detail-section-title">Tasks</h2>
            <label className="dashboard-period">
              <span className="dashboard-period__label">Month</span>
              <select
                className="field-input field-input--compact"
                value={chartMonth}
                onChange={(e) => setChartMonth(e.target.value)}
                disabled={monthOptions.length === 0}
              >
                {monthOptions.length === 0 ? (
                  <option value="">No months</option>
                ) : (
                  monthOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                )}
              </select>
            </label>
          </div>
          <DashboardTaskTitlePie
            embedded
            chartMonth={chartMonth}
            onMonthChange={setChartMonth}
            monthOptions={monthOptions}
            scopeTasks={scopeTasks}
            hideMonthSelect
          />
        </section>

        <section className="card dashboard-panel dashboard-panel--tasks-list">
          <div className="dashboard-split__toolbar">
            <label className="dashboard-split__search">
              <span className="sr-only">Search tasks</span>
              <input
                type="search"
                className="field-input field-input--compact"
                placeholder="Search tasks…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
              />
            </label>
            {canCreateTask ? (
              <button
                type="button"
                className="btn-primary btn-sm dashboard-split__add"
                aria-label="Add task"
                title="Add task"
                onClick={() => setCreateOpen(true)}
              >
                +
              </button>
            ) : null}
          </div>
          <DashboardTaskList
            tasks={tasks}
            selectedYmd={null}
            calendarMonthKey={searchTrimmed ? null : chartMonth || null}
            searchQuery={searchTrimmed}
            embedded
          />
        </section>
      </div>
      <TaskFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}
